
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCw, Image, CheckCircle2 } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { SystemSettings as SystemSettingsType } from "@/types/database";
import { Separator } from "@/components/ui/separator";
import { ensureValidToken } from "@/services/AuthAwareApiService";

export function SystemSettings() {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [isProxyingLogos, setIsProxyingLogos] = useState(false);
  const [proxyStats, setProxyStats] = useState<{ processed: number, total: number, lastUpdated: string | null }>({ processed: 0, total: 0, lastUpdated: null });
  

  // Fetch logo stats to show processed/total counts
  const { data: logoStats, isLoading: isLoadingLogoStats } = useQuery({
    queryKey: ["logo-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("investment_institutions")
        .select("id, logo_url, logo_proxied_url", { count: "exact" });

      if (error) throw error;
      
      const total = data.length;
      const processed = data.filter(item => item.logo_proxied_url).length;
      const lastUpdated = (data?.length || 0) > 0 ? 
        new Date().toISOString() : 
        null;
      
      setProxyStats({ total, processed, lastUpdated });
      return { total, processed, lastUpdated };
    },
    refetchOnWindowFocus: false
  });

  // Fetch system settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ["system-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("system_settings")
        .select("*")
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          // No settings found, create default settings
          const { data: newData, error: insertError } = await supabase
            .from("system_settings")
            .insert([{ signup_enabled: true }])
            .select()
            .single();

          if (insertError) throw insertError;
          return newData as SystemSettingsType;
        }
        throw error;
      }
      return data as SystemSettingsType;
    },
  });

  // Update system settings
  const updateSettings = useMutation({
    mutationFn: async (signupEnabled: boolean) => {
      setIsSaving(true);
      const { error } = await supabase
        .from("system_settings")
        .update({ signup_enabled: signupEnabled, updated_at: new Date().toISOString() })
        .eq("id", (settings as SystemSettingsType)?.id);

      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      toast.success("Settings updated successfully");
      queryClient.invalidateQueries({ queryKey: ["system-settings"] });
    },
    onError: (error) => {
      toast.error(`Failed to update settings: ${error.message}`);
    },
    onSettled: () => {
      setIsSaving(false);
    },
  });

  const handleToggleSignup = (enabled: boolean) => {
    updateSettings.mutate(enabled);
  };

  // Mutation for proxying institution logos
  const proxyLogos = useMutation({
    mutationFn: async () => {
      setIsProxyingLogos(true);
      
      // Get current stats to determine how many are left to process
      const { data: institutions, error: statsError } = await supabase
        .from("investment_institutions")
        .select("id, logo_url, logo_proxied_url");
      
      if (statsError) throw statsError;
      
      const total = institutions.length;
      const processed = institutions.filter(item => item.logo_proxied_url).length;
      const remaining = total - processed;
      
      // No need to process if everything is already done
      if (remaining === 0) {
        return { successful: 0, failed: 0, message: "All logos already processed" };
      }
      
      // Get batch size - Edge Function has a default limit of 50
      const BATCH_SIZE = 50;
      let totalSuccessful = 0;
      let totalFailed = 0;
      
      // Process in batches until all are done or we hit an error
      let stillHasUnprocessed = true;
      let iteration = 0;
      const MAX_ITERATIONS = Math.ceil(remaining / BATCH_SIZE) + 1; // Add 1 for safety
      
      while (stillHasUnprocessed && iteration < MAX_ITERATIONS) {
        iteration++;
        
        // Update the stats with current progress
        setProxyStats(prev => ({
          ...prev,
          processed: processed + totalSuccessful,
          total
        }));
        
        // Ensure valid token before making API call
        await ensureValidToken('proxy-provider-logos');
        
        // Call the Edge Function to process a batch
        const { data: batchResult, error: batchError } = await supabase.functions.invoke('proxy-provider-logos', {
          body: {
            provider_type: 'all',   // Process all provider types
            force_refresh: false,   // Skip logos that already have a proxied URL
            limit: BATCH_SIZE       // Process in limited batches (Edge Function default is 50)
          }
        });
        
        if (batchError) throw batchError;
        
        // Add to our totals
        totalSuccessful += batchResult.successful || 0;
        totalFailed += batchResult.failed || 0;
        
        // Check if we processed any in this batch
        const processedInBatch = (batchResult.successful || 0) + (batchResult.failed || 0);
        
        // If nothing was processed in this batch, or we've done all remaining, we're done
        if (processedInBatch === 0 || totalSuccessful + totalFailed >= remaining) {
          stillHasUnprocessed = false;
        }
        
        // Add a small delay between batches
        if (stillHasUnprocessed) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      return {
        successful: totalSuccessful,
        failed: totalFailed,
        message: `Processed ${totalSuccessful + totalFailed} institutions in ${iteration} batches`
      };
    },
    onSuccess: (data) => {
      const { successful, failed, message } = data as { successful: number, failed: number, message: string };
      
      // Update the lastUpdated timestamp
      setProxyStats(prev => ({
        ...prev,
        lastUpdated: new Date().toISOString()
      }));
      
      // Refresh the logo stats to show updated counts
      queryClient.invalidateQueries({ queryKey: ["logo-stats"] });
      
      if (successful === 0 && failed === 0) {
        toast.success(
          <div className="space-y-2">
            <p>All institution logos are already processed</p>
            <p className="text-sm text-muted-foreground">
              No additional processing needed
            </p>
          </div>
        );
      } else {
        toast.success(
          <div className="space-y-2">
            <p>Institution logo processing complete</p>
            <p className="text-sm text-muted-foreground">
              Processed {successful + failed} logos ({successful} successful, {failed} failed)
            </p>
            {message.includes('batches') && (
              <p className="text-xs text-muted-foreground">
                {message}
              </p>
            )}
          </div>
        );
      }
    },
    onError: (error) => {
      toast.error(`Failed to proxy institution logos: ${error instanceof Error ? error.message : String(error)}`);
    },
    onSettled: () => {
      setIsProxyingLogos(false);
    }
  });

  const handleProxyLogos = () => {
    proxyLogos.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Spinner size="md" className="text-primary" ariaLabel="Loading settings" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>System Settings</CardTitle>
        <CardDescription>Configure global system settings</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between space-x-2">
          <div className="space-y-0.5">
            <Label htmlFor="signup-toggle">User Registration</Label>
            <p className="text-sm text-muted-foreground">
              Allow new users to create accounts. Existing users can always sign in.
            </p>
          </div>
          <div className="flex items-center">
            {isSaving ? (
              <Spinner size="sm" className="mr-2" ariaLabel="Saving" />
            ) : null}
            <Switch
              id="signup-toggle"
              checked={(settings as SystemSettingsType)?.signup_enabled}
              onCheckedChange={handleToggleSignup}
              disabled={isSaving}
              aria-label="Toggle signup enabled"
            />
          </div>
        </div>
        
        <Separator className="my-4" />
        
        <div className="space-y-4">
          <div className="space-y-0.5">
            <Label>Institution Logos</Label>
            <p className="text-sm text-muted-foreground">
              Process institution logos to create CORS-safe versions for UI display
            </p>
          </div>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <Button 
                  onClick={handleProxyLogos} 
                  disabled={isProxyingLogos}
                  className="flex items-center"
                  size="sm"
                >
                  {isProxyingLogos ? (
                    <>
                      <Spinner size="sm" className="mr-2" ariaLabel="Processing" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Image className="h-4 w-4 mr-2" />
                      Proxy Institution Logos
                    </>
                  )}
                </Button>
              </div>
              
              {isLoadingLogoStats ? (
                <div className="flex items-center text-xs text-muted-foreground">
                  <Spinner size="xs" className="mr-1" ariaLabel="Loading stats" /> Loading stats...
                </div>
              ) : (
                <div className="flex items-center text-xs">
                  <div className="flex items-center">
                    <span className="font-medium">{proxyStats.processed}</span>
                    <span className="text-muted-foreground mx-1">of</span>
                    <span className="font-medium">{proxyStats.total}</span>
                    <span className="text-muted-foreground ml-1">processed</span>
                    {proxyStats.processed === proxyStats.total && proxyStats.total > 0 && (
                      <CheckCircle2 className="h-3 w-3 text-green-500 ml-1" />
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {!isLoadingLogoStats && proxyStats.processed > 0 && proxyStats.total > 0 && (
              <div className="w-full bg-secondary rounded-full h-1.5">
                <div 
                  className="bg-primary h-1.5 rounded-full" 
                  style={{ width: `${(proxyStats.processed / proxyStats.total) * 100}%` }}
                ></div>
              </div>
            )}
            
            <p className="text-xs text-muted-foreground">
              This operation finds institution logos from external sources, stores them in Supabase storage, 
              and updates the database with CORS-safe URLs. This ensures logos display properly in the UI.
              {proxyStats.lastUpdated && (
                <span className="block mt-1">
                  Last updated: {new Date(proxyStats.lastUpdated).toLocaleString()}
                </span>
              )}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
